import React, { useEffect, useMemo, useRef, useState } from "react";

import { motion } from "framer-motion";
import { cn } from "lib/utils";
import {
  Check,
  ChevronRight,
  ClipboardCopy,
  Clock,
  Copy,
  Loader2,
  MapPin,
  Share2,
} from "lucide-react";
import { toast } from "utils/toast";

import { fadeInUp } from "js/components/Quotas/animations";
import { Button } from "js/components/ui/button";

import { PaymentData } from "js/pages/Payments/types";

interface PaynetReceiptProps {
  paymentData: PaymentData;
  amount: number;
  onCancel?: () => void;
  cancelLoading?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(amount);

const formatShortDeadline = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ------------------------------------------------------------------ */
/*  Countdown hook                                                     */
/* ------------------------------------------------------------------ */

interface CountdownState {
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

function useCountdown(dueDateStr?: string) {
  const [remaining, setRemaining] = useState<CountdownState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!dueDateStr) return;
    const dueDate = new Date(dueDateStr);
    const update = () => {
      const diff = dueDate.getTime() - Date.now();
      if (diff <= 0) {
        setRemaining({ hours: 0, minutes: 0, seconds: 0, totalMs: 0 });
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      const hours = Math.floor(diff / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      const seconds = Math.floor((diff % 60_000) / 1_000);
      setRemaining({ hours, minutes, seconds, totalMs: diff });
    };
    update();
    intervalRef.current = setInterval(update, 1_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [dueDateStr]);

  return remaining;
}

/* ------------------------------------------------------------------ */
/*  Deadline indicator (same pattern as SpeiPaymentSlip)               */
/* ------------------------------------------------------------------ */

const DeadlineIndicator: React.FC<{
  countdown: CountdownState;
  dueDateStr?: string;
  timerColor: string;
}> = ({ countdown, dueDateStr, timerColor }) => {
  const isLive = countdown.totalMs <= 2 * 3_600_000;

  if (countdown.totalMs <= 0) {
    return (
      <div className="flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-red-600">
        <Clock className="size-3" />
        <span className="text-xs font-medium">
          Vencido{dueDateStr ? ` · ${formatShortDeadline(dueDateStr)}` : ""}
        </span>
      </div>
    );
  }

  if (isLive) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-full border px-3 py-1",
          countdown.totalMs <= 3_600_000
            ? "border-red-200 bg-red-50 text-red-600"
            : "border-amber-200 bg-amber-50 text-amber-600",
        )}
      >
        <Clock className="size-3.5" />
        <span className="font-mono text-xs font-bold">
          {String(countdown.hours).padStart(2, "0")}:
          {String(countdown.minutes).padStart(2, "0")}:
          {String(countdown.seconds).padStart(2, "0")}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs",
        timerColor,
        "border-border/60 bg-white/60",
      )}
    >
      <Clock className="size-3" />
      <span>Vence: {dueDateStr ? formatShortDeadline(dueDateStr) : ""}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  CopyField                                                          */
/* ------------------------------------------------------------------ */

const CopyField: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  stripSpaces?: boolean;
}> = ({ label, value, mono = true, stripSpaces = false }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        stripSpaces ? value.replace(/\s/g, "") : value,
      );
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "group flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-all",
        copied
          ? "border-emerald-200 bg-emerald-50"
          : "border-border/60 bg-muted/30 hover:border-border hover:bg-muted/50 active:bg-muted/70",
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            "block truncate text-sm font-semibold text-foreground mt-0.5",
            mono && "font-mono tracking-wide",
          )}
        >
          {value}
        </span>
      </div>
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md transition-colors",
          copied
            ? "bg-emerald-100 text-emerald-600"
            : "text-muted-foreground/50 group-hover:bg-muted group-hover:text-foreground",
        )}
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-3.5" />}
      </span>
    </button>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const PaynetReceipt: React.FC<PaynetReceiptProps> = ({
  paymentData,
  amount,
  onCancel,
  cancelLoading,
}) => {
  const countdown = useCountdown(paymentData.due_date);
  const [allCopied, setAllCopied] = useState(false);

  const timerColor = useMemo(() => {
    if (!countdown) return "text-muted-foreground";
    if (countdown.totalMs > 24 * 3_600_000) return "text-emerald-600";
    if (countdown.totalMs > 2 * 3_600_000) return "text-amber-600";
    return "text-red-600";
  }, [countdown]);

  const waText = [
    "\u{1F3EA} *Referencia de pago Paynet*",
    `\u{1F522} Referencia: ${paymentData.reference}`,
    `\u{1F4B0} Monto: ${formatCurrency(amount)} MXN`,
    paymentData.due_date
      ? `\u23F0 Fecha l\u00EDmite: ${formatDate(paymentData.due_date)}`
      : "",
    "",
    "Muestra el c\u00F3digo en tienda o dicta la referencia.",
  ]
    .filter(Boolean)
    .join("\n");

  const handleCopyAll = async () => {
    try {
      const lines = [
        "Referencia de pago Paynet",
        `Referencia: ${paymentData.reference}`,
        `Monto: ${formatCurrency(amount)} MXN`,
        paymentData.due_date
          ? `Fecha limite: ${formatDate(paymentData.due_date)}`
          : "",
      ].filter(Boolean);
      await navigator.clipboard.writeText(lines.join("\n"));
      setAllCopied(true);
      toast.success("Todos los datos copiados");
      setTimeout(() => setAllCopied(false), 2_500);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible">
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        {/* ── Header: amount + deadline ───────────────────────── */}
        <div className="border-b border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 to-blue-50/60 px-5 py-4 dark:border-indigo-900 dark:from-indigo-950/30 dark:to-blue-950/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
                Paga exactamente
              </p>
              <p className="mt-1.5 text-3xl font-bold tracking-tight text-indigo-950 dark:text-indigo-100">
                {formatCurrency(amount)}
              </p>
            </div>
            {countdown && (
              <DeadlineIndicator
                countdown={countdown}
                dueDateStr={paymentData.due_date}
                timerColor={timerColor}
              />
            )}
          </div>
        </div>

        {/* ── Payment details ──────────────────────────────────── */}
        <div className="flex flex-col gap-2.5 p-4">
          <p className="text-xs text-muted-foreground">
            Muestra el código de barras o dicta la referencia en tienda:
          </p>

          {paymentData.reference && (
            <CopyField
              label="Número de referencia"
              value={paymentData.reference}
              stripSpaces
            />
          )}

          {paymentData.barcode_url && (
            <div className="flex justify-center rounded-lg border border-border/60 bg-white p-2">
              <img
                src={paymentData.barcode_url}
                alt="Código de barras"
                className="h-20 w-full object-contain"
              />
            </div>
          )}

          <div className="px-3.5 py-1">
            <span className="block text-[11px] font-medium text-muted-foreground">
              Importe
            </span>
            <span className="block text-sm font-bold text-foreground mt-0.5">
              {formatCurrency(amount)} MXN
            </span>
          </div>
        </div>

        {/* ── Available stores ─────────────────────────────────── */}
        <div className="border-t border-border/60 px-4 py-3">
          <a
            href="https://www.paynet.com.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/30 px-3.5 py-2.5 transition-colors hover:border-border hover:bg-muted/50 !no-underline"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <MapPin className="size-4 text-primary" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-foreground">
                Encontrar tienda cercana
              </span>
              <span className="block text-[11px] text-muted-foreground">
                +20,000 puntos de pago en México
              </span>
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </a>
        </div>

        {/* ── Instructions ─────────────────────────────────────── */}
        <div className="border-t border-border/60 px-4 py-3">
          <p className="mb-2 text-xs font-semibold">Instrucciones</p>
          <ol className="flex flex-col gap-1.5">
            {[
              "Ve a cualquier tienda afiliada",
              "Muestra el código de barras o dicta el número de referencia",
              "Paga el monto exacto",
              "¡Listo! Recibirás confirmación en minutos",
            ].map((step, i) => (
              <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        {/* ── Action buttons ───────────────────────────────────── */}
        <div className="flex flex-col gap-2.5 border-t border-border/60 bg-muted/20 p-4">
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full gap-2 transition-all",
              allCopied &&
                "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700",
            )}
            onClick={handleCopyAll}
          >
            {allCopied ? (
              <>
                <Check className="size-4" />
                Datos copiados
              </>
            ) : (
              <>
                <ClipboardCopy className="size-4" />
                Copiar todos los datos
              </>
            )}
          </Button>

          <a
            href={`https://wa.me/?text=${encodeURIComponent(waText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 rounded-lg !border !border-green-200 !bg-green-50 py-2.5 text-sm font-medium !text-green-700 !no-underline transition-colors hover:!bg-green-100 dark:!border-green-800 dark:!bg-green-950 dark:!text-green-400 dark:hover:!bg-green-900"
          >
            <Share2 className="size-4" />
            Compartir por WhatsApp
          </a>

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onCancel}
              disabled={cancelLoading}
            >
              {cancelLoading && <Loader2 className="size-4 animate-spin" />}
              Cambiar método de pago
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PaynetReceipt;
